import { createContext, useState, useEffect, useCallback } from "react";
import { getApiUrl, getAuthHeaders } from "../utils/api";

export const UserContext = createContext();

function readStoredUser() {
  try {
    const storedUser = localStorage.getItem("user");
    return storedUser ? JSON.parse(storedUser) : null;
  } catch {
    return null;
  }
}

const SESSION_KEYS = ["_id", "id", "role", "username", "name", "avatar", "badge", "email"];

function userSessionChanged(prev, next) {
  if (!prev || !next) return prev !== next;
  return SESSION_KEYS.some(
    (key) => String(prev[key] ?? "") !== String(next[key] ?? "")
  );
}

export const UserProvider = ({ children }) => {
  const [user, setUserState] = useState(null);
  const [userLoading, setUserLoading] = useState(true);

  const setUser = useCallback((nextUser) => {
    if (nextUser) {
      localStorage.setItem("user", JSON.stringify(nextUser));
      setUserState(nextUser);
    } else {
      localStorage.removeItem("user");
      setUserState(null);
    }
  }, []);

  const refreshUser = useCallback(async () => {
    const stored = readStoredUser();
    if (!stored) {
      setUserLoading(false);
      return null;
    }

    try {
      const res = await fetch(getApiUrl("/api/user/profile"), {
        credentials: "include",
        headers: getAuthHeaders(),
      });

      if (!res.ok) {
        if (res.status === 401 || res.status === 403) {
          const data = await res.json().catch(() => ({}));
          const msg = String(data.msg || data.error || "").toLowerCase();
          const hasToken = !!localStorage.getItem("token");
          if (
            !hasToken ||
            msg.includes("jwt expired") ||
            msg.includes("invalid token") ||
            msg.includes("no token")
          ) {
            setUser(null);
          }
        }
        return stored;
      }

      const data = await res.json();
      if (data?.user) {
        const merged = { ...stored, ...data.user };
        if (userSessionChanged(stored, merged)) {
          setUser(merged);
        }
        return merged;
      }
    } catch {
      setUserState(stored);
      return stored;
    } finally {
      setUserLoading(false);
    }

    return stored;
  }, [setUser]);

  useEffect(() => {
    const stored = readStoredUser();
    if (stored) {
      setUserState(stored);
      refreshUser();
    } else {
      setUserLoading(false);
    }
  }, [refreshUser]);

  useEffect(() => {
    const syncOnFocus = () => {
      if (document.visibilityState === "visible" && readStoredUser()) {
        refreshUser();
      }
    };

    document.addEventListener("visibilitychange", syncOnFocus);
    return () => document.removeEventListener("visibilitychange", syncOnFocus);
  }, [refreshUser]);

  return (
    <UserContext.Provider value={{ user, setUser, refreshUser, userLoading }}>
      {children}
    </UserContext.Provider>
  );
};
